import { CircularProgress } from '@mui/material'
import { Box } from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { L, Icon } from '../ui/LavDropdown'
import { LavSectionTitle } from '../add-appointment/appointmentLavenderFields'
import { getFullName } from '../../utils/clientUtils'
import { getPaymentMethodLabel } from '../../utils/paymentMethodUtils'
import { usePetAvatars } from '../bookings/usePetAvatars'
import { MbIc } from './mbBits'
import { getServicePrice } from '../../utils/appointmentUtils'
import { MovedBanner } from './rescheduleShared'

dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * Shared, presentation-only building blocks for the Appointment Details screens
 * (Claude Design "Appointment details" spec). MobileAppointmentDetails and
 * DesktopAppointmentDetails both render <AppointmentSections>; only the header
 * chrome and footer/action affordances differ between the two form factors.
 */

export const DANGER = '#BE4B45'
export const DANGER_SOFT = '#FBECEB'
export const WARN_IC = '#C08A2D'
export const WARN_BG = '#FDF3E4'
export const WARN_BORDER = '#F2DDB4'
export const WARN_TEXT = '#8F6217'
export const OK_BG = '#E9F6EE'
export const OK_BORDER = '#C9E8D4'
export const OK_TEXT = '#25714A'
export const OK_IC = '#2F8A5B'

const addOnsSum = (addOns) => (Array.isArray(addOns) ? addOns : []).reduce((s, a) => s + (a.price || 0), 0)

/**
 * Normalize a single or grouped appointment into the display shape both
 * form factors render. Returns entries + derived when/price/name values.
 */
export function computeApptDisplay({ appointment, client, pet, service, groupedAppointments = [], tz }) {
	// `petId` lets the pet card link through to that pet's profile.
	const entries = groupedAppointments.length > 0
		? groupedAppointments.map(a => ({
			key: a.id,
			petId: a.pets?.id || a.pet_id || null,
			petName: a.pets?.name,
			petSub: a.pets?.species || '',
			serviceName: a.service_types?.name,
			price: getServicePrice(a) + addOnsSum(a.add_ons),
			addOns: Array.isArray(a.add_ons) ? a.add_ons : [],
			mins: Math.max(0, dayjs(a.end_ts).diff(dayjs(a.start_ts), 'minute')),
			employee: null,
		}))
		: [{
			key: appointment?.id,
			petId: pet?.id || appointment?.pet_id || null,
			petName: pet?.name,
			petSub: [pet?.breed, pet?.species].filter(Boolean).join(' | '),
			serviceName: service?.name,
			price: getServicePrice(appointment, service) + addOnsSum(appointment?.add_ons),
			addOns: Array.isArray(appointment?.add_ons) ? appointment.add_ons : [],
			mins: Math.max(0, dayjs(appointment?.end_ts).diff(dayjs(appointment?.start_ts), 'minute')),
			employee: appointment?.employees
				? `${appointment.employees.first_name || ''} ${appointment.employees.last_name || ''}`.trim()
				: null,
		}]

	const firstStart = groupedAppointments.length > 0 ? groupedAppointments[0].start_ts : appointment?.start_ts
	const lastEnd = groupedAppointments.length > 0
		? groupedAppointments[groupedAppointments.length - 1].end_ts
		: appointment?.end_ts
	const start = dayjs(firstStart).tz(tz)
	const end = dayjs(lastEnd).tz(tz)
	const totalMins = Math.max(0, end.diff(start, 'minute'))
	const totalPrice = entries.reduce((s, e) => s + e.price, 0)
	const petNames = entries.map(e => e.petName).filter(Boolean).join(' & ') || appointment?.customer_name || ''
	const whenShort = `${start.format('ddd, MMM D')} at ${start.format('h:mm A')}`

	return { entries, start, end, totalMins, totalPrice, petNames, whenShort }
}

/* ── status banner (completed / no-show / canceled) ── */
export function StatusBanner({ status, paidRecord, tz, formatCurrency, onUndo, updating, dense = false }) {
	const palette = status === 'completed'
		? { bg: OK_BG, border: OK_BORDER, color: OK_TEXT, ic: OK_IC }
		: status === 'noShow'
			? { bg: WARN_BG, border: WARN_BORDER, color: WARN_TEXT, ic: WARN_IC }
			: { bg: '#EFEDF5', border: '#DDD8EC', color: '#6E6889', ic: '#8B85A4' }

	if (status !== 'completed' && status !== 'noShow' && status !== 'cancelled') return null

	return (
		<Box sx={{
			display: 'flex', alignItems: 'center', gap: '11px', borderRadius: '15px',
			p: '13px 14px', border: `1.5px solid ${palette.border}`, bgcolor: palette.bg, color: palette.color,
		}}>
			<Box sx={{ flex: 'none', display: 'flex', color: palette.ic }}>
				{status === 'completed'
					? <MbIc name="checkc" size={17} />
					: status === 'noShow'
						? <MbIc name="warn" size={17} />
						: <MbIc name="ban" size={16} />}
			</Box>
			<Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1px' }}>
				<Box component="strong" sx={{ fontSize: dense ? '12.5px' : '14.5px', fontWeight: dense ? 650 : 750 }}>
					{status === 'completed'
						? paidRecord
							? `Checked out · ${formatCurrency(paidRecord.total_amount)} paid`
							: 'Checked out'
						: status === 'noShow'
							? 'Marked as no-show'
							: 'Appointment canceled'}
				</Box>
				{status === 'completed' && paidRecord && (
					<Box component="span" sx={{ fontSize: dense ? '12px' : '12.5px', fontWeight: 600, opacity: 0.85 }}>
						{getPaymentMethodLabel(paidRecord.payment_method)} · {dayjs(paidRecord.created_at).tz(tz).format('MMM D, h:mm A')}
					</Box>
				)}
			</Box>
			{(status === 'noShow' || status === 'cancelled') && (
				<Box
					component="button"
					type="button"
					onClick={onUndo}
					disabled={updating}
					sx={{
						display: 'flex', alignItems: 'center', gap: '5px', flex: 'none',
						border: '1.5px solid currentColor', bgcolor: 'transparent', color: 'inherit',
						fontFamily: 'inherit', fontSize: dense ? '12px' : '13px', fontWeight: 750, p: '7px 12px',
						borderRadius: '10px', opacity: 0.9, cursor: 'pointer',
						'&:hover': { bgcolor: 'rgba(46, 42, 64, 0.06)' },
						'&:disabled': { opacity: 0.5, cursor: 'default' },
					}}
				>
					{updating ? <CircularProgress size={13} sx={{ color: 'inherit' }} /> : <MbIc name="undo" size={14} />}
					<span>Undo</span>
				</Box>
			)}
		</Box>
	)
}

/* ── per-pet card (design ApdPetCard) ── */
// With `onOpen` the whole card becomes a link to that pet's profile (design
// `.ap-petcard.apd-link` + the `.apd-go` chevron).
export function PetServiceCard({ entry, formatCurrency, onOpen, avatarUrl = null, dense = false }) {
	const linkable = Boolean(onOpen && entry.petId)
	const open = () => onOpen(entry)

	return (
		<Box
			{...(linkable ? {
				role: 'button',
				tabIndex: 0,
				title: `View ${entry.petName || 'this pet'}’s profile`,
				onClick: open,
				onKeyDown: (e) => {
					if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open() }
				},
			} : {})}
			sx={{
				position: 'relative',
				border: `1px solid ${L.border}`, borderRadius: '15px', p: '13px', bgcolor: L.field,
				display: 'flex', flexDirection: 'column', gap: '10px',
				...(linkable ? {
					cursor: 'pointer', transition: 'border-color .12s, box-shadow .12s',
					'&:hover, &:focus-visible': {
						borderColor: L.primary,
						boxShadow: `0 0 0 3px ${L.primarySoft}`,
						outline: 'none',
					},
					'&:hover .apd-go, &:focus-visible .apd-go': { color: L.primaryDeep },
				} : {}),
			}}
		>
			{linkable && (
				<Box
					className="apd-go"
					component="span"
					aria-hidden="true"
					sx={{
						position: 'absolute', top: 13, right: 13, display: 'flex',
						color: L.muted, transition: 'color .12s',
						// the shared chevron asset points down — turn it into a "go" arrow
						transform: 'rotate(-90deg)',
					}}
				>
					<Icon name="chevron" size={14} />
				</Box>
			)}
			<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', pr: linkable ? '20px' : 0 }}>
				<Box sx={{
					width: 40, height: 40, flex: 'none', borderRadius: '50%', bgcolor: L.primarySoft,
					color: L.primaryDeep, display: 'flex', alignItems: 'center', justifyContent: 'center',
					overflow: 'hidden',
				}}>
					{avatarUrl ? (
						<Box
							component="img"
							src={avatarUrl}
							alt={entry.petName ? `${entry.petName}’s photo` : 'Pet photo'}
							sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
						/>
					) : (
						<Icon name="paw" size={17} />
					)}
				</Box>
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Box component="strong" sx={{ display: 'block', fontSize: dense ? '15px' : '16px', fontWeight: 750, color: L.text }}>
						{entry.petName || 'Pet'}
					</Box>
					{entry.petSub && (
						<Box component="span" sx={{
							display: 'block', fontSize: dense ? '12px' : '12.5px', color: L.muted, mt: '1px',
							whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
						}}>
							{entry.petSub}
						</Box>
					)}
				</Box>
				<Box component="span" sx={{ flex: 'none', fontSize: dense ? '13.5px' : '15px', fontWeight: 750, fontVariantNumeric: 'tabular-nums', color: L.text }}>
					{formatCurrency(entry.price)}
				</Box>
			</Box>
			<Box sx={{ bgcolor: L.surface, borderRadius: '11px', p: '10px 12px', fontSize: dense ? '13px' : '13.5px', fontWeight: 650, color: L.text }}>
				<Box>{entry.serviceName || '—'}</Box>
				{/* One line per add-on, per the design's `.ap-petcard-adds` rows. */}
				{entry.addOns.map((a, i) => (
					<Box key={a.service_id || a.name || i} sx={{ color: L.muted, fontWeight: 550, fontSize: '12.5px', mt: '3px' }}>
						+ {a.name}
					</Box>
				))}
			</Box>
			<Box sx={{ fontSize: dense ? '12.5px' : '13px', color: L.muted, fontWeight: 600 }}>
				{entry.mins} mins{entry.employee ? ` · ${entry.employee}` : ''}
			</Box>
		</Box>
	)
}

/**
 * The shared body of the appointment details view: status banner, When card,
 * Client card, per-pet service cards, notes, and total. Form-factor-agnostic —
 * both mobile and desktop wrap this with their own header + footer chrome.
 */
export function AppointmentSections({
	appointment,
	client,
	display,
	status,
	paidRecord,
	tz,
	formatCurrency,
	onUndo,
	updatingStatus,
	dimmed = false,
	onOpenClientProfile,
	onOpenPetProfile,
	showPetCount = false,
	// Desktop renders the design's compact type scale (.ap-* / .apd-* in
	// Appointment Details.html); mobile keeps the larger .mbapd overrides.
	dense = false,
	// Action row at the foot of the When card (design .apd-when-act /
	// .mbapd-when-act): { label, icon, onClick } — e.g. Reschedule.
	whenAction = null,
	// After a reschedule: { fromStartTs, notified } → the moved-from evidence
	// bar (design AprMovedBar / AprMovedBanner).
	moved = null,
}) {
	const { entries, start, end, totalMins, totalPrice } = display
	// The appointment queries carry pet_id but no photo — resolve avatars from
	// pet_photos in one batch (same pattern as the Bookings lists); pets
	// without a photo keep the paw placeholder.
	const petAvatars = usePetAvatars(entries.map(e => e.petId))
	const sectionSx = { opacity: dimmed ? 0.5 : 1 }
	const clientLinkable = Boolean(onOpenClientProfile && client?.id)
	const openClient = () => onOpenClientProfile(client)
	// Mobile puts the visit's pet count beside the name (design `clientSub`);
	// desktop shows the name alone.
	const petCount = entries.length

	return (
		<>
			<StatusBanner
				dense={dense}
				status={status}
				paidRecord={paidRecord}
				tz={tz}
				formatCurrency={formatCurrency}
				onUndo={onUndo}
				updating={updatingStatus}
			/>
			<MovedBanner moved={moved} clientName={getFullName(client) || appointment?.customer_name} tz={tz} dense={dense} />

			<Box component="section" sx={sectionSx}>
				<LavSectionTitle>When</LavSectionTitle>
				<Box sx={{ bgcolor: L.surface, border: `1px solid ${L.border}`, borderRadius: '15px', p: '6px 0' }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '11px', p: '10px 15px', fontSize: dense ? '13px' : '14.5px', fontWeight: 650, '& svg': { color: L.primary, flex: 'none' } }}>
						<Icon name="calendar" size={16} />
						<span>{start.format('dddd, MMMM D, YYYY')}</span>
					</Box>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '11px', p: '10px 15px', fontSize: dense ? '13px' : '14.5px', fontWeight: 650, '& svg': { color: L.primary, flex: 'none' } }}>
						<Icon name="clock" size={16} />
						<Box component="span">
							{start.format('h:mm A')} – {end.format('h:mm A')}{' '}
							<Box component="em" sx={{ fontStyle: 'normal', color: L.muted, fontWeight: 600 }}>· {totalMins} mins</Box>
						</Box>
					</Box>
					{whenAction && (
						<Box
							component="button"
							type="button"
							onClick={whenAction.onClick}
							sx={{
								display: 'flex', alignItems: 'center', gap: dense ? '8px' : '10px', width: '100%',
								m: '6px 0 -6px', px: dense ? '14px' : '15px', height: dense ? 40 : 50,
								border: 'none', borderTop: `1px solid ${L.border}`, bgcolor: 'transparent', color: L.primaryDeep,
								fontFamily: 'inherit', fontSize: dense ? '13px' : '14.5px', fontWeight: 700, textAlign: 'left',
								borderRadius: '0 0 15px 15px', cursor: 'pointer', transition: 'background-color .12s ease',
								'&:hover': { bgcolor: L.primarySoft }, '&:active': { bgcolor: L.primarySoft },
							}}
						>
							<Box sx={{ display: 'flex', flex: 'none', color: L.primary }}>
								<MbIc name={whenAction.icon || 'move'} size={dense ? 15 : 16} />
							</Box>
							<span>{whenAction.label}</span>
							<Box component="span" sx={{ ml: 'auto', display: 'flex', transform: 'rotate(-90deg)', color: L.primary }}>
								<Icon name="chevron" size={dense ? 14 : 15} />
							</Box>
						</Box>
					)}
				</Box>
			</Box>

			{client && (
				<Box component="section" sx={sectionSx}>
					<LavSectionTitle>Client</LavSectionTitle>
					{/* Links through to the client's profile (design `.apd-link` row
					    with its trailing "View profile" affordance). */}
					<Box
						{...(clientLinkable ? {
							role: 'button',
							tabIndex: 0,
							title: `View ${getFullName(client)}’s client profile`,
							onClick: openClient,
							onKeyDown: (e) => {
								if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openClient() }
							},
						} : {})}
						sx={{
							display: 'flex', alignItems: 'center', gap: '11px', bgcolor: L.surface,
							border: `1.5px solid ${L.border}`, borderRadius: '12px', minHeight: 50,
							p: '4px 15px', color: L.muted,
							...(clientLinkable ? {
								cursor: 'pointer', transition: 'border-color .12s, box-shadow .12s',
								'&:hover, &:focus-visible': {
									borderColor: L.primary,
									boxShadow: `0 0 0 3px ${L.primarySoft}`,
									outline: 'none',
								},
								'&:hover .apd-profile, &:focus-visible .apd-profile': { color: L.primaryDeep },
							} : {}),
						}}
					>
						<Icon name="user" size={16} />
						<Box component="span" sx={{
							flex: 1, minWidth: 0, fontSize: dense ? '13.5px' : '15px', fontWeight: dense ? 600 : 650, color: L.text,
							whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
						}}>
							{getFullName(client)}
							{showPetCount && petCount > 0 && (
								<Box component="em" sx={{ fontStyle: 'normal', color: L.muted, fontWeight: 600 }}>
									{` · ${petCount} ${petCount === 1 ? 'pet' : 'pets'}`}
								</Box>
							)}
						</Box>
						{clientLinkable && (
							<Box
								className="apd-profile"
								component="span"
								sx={{
									flex: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px',
									fontSize: '12.5px', fontWeight: 700, color: L.muted, transition: 'color .12s',
								}}
							>
								<Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>View profile</Box>
								<Box component="span" sx={{ display: 'flex', transform: 'rotate(-90deg)' }}>
									<Icon name="chevron" size={13} />
								</Box>
							</Box>
						)}
					</Box>
				</Box>
			)}

			<Box component="section" sx={sectionSx}>
				<LavSectionTitle>Pets & services</LavSectionTitle>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
					{entries.map(e => (
						<PetServiceCard
							key={e.key}
							dense={dense}
							entry={e}
							avatarUrl={e.petId ? petAvatars[e.petId] : null}
							formatCurrency={formatCurrency}
							onOpen={onOpenPetProfile}
						/>
					))}
				</Box>
			</Box>

			{appointment?.comments && (
				<Box component="section" sx={sectionSx}>
					<LavSectionTitle>Notes</LavSectionTitle>
					<Box sx={{ bgcolor: L.surface, borderRadius: '12px', p: '12px 13px', fontSize: dense ? '13px' : '14px', lineHeight: dense ? 1.45 : 1.5, color: L.text }}>
						{appointment.comments}
					</Box>
				</Box>
			)}

			<Box component="section" sx={sectionSx}>
				<Box sx={{
					display: 'flex', alignItems: 'center', gap: '8px',
					borderTop: `1.5px solid ${L.border}`, pt: '14px', fontSize: dense ? '14px' : '15px',
				}}>
					<Box component="span" sx={{ color: L.muted, fontWeight: 600 }}>Total:</Box>
					<Box component="strong" sx={{ ml: 'auto', fontSize: dense ? '15.5px' : '17px', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
						{formatCurrency(totalPrice)} ({totalMins} mins)
					</Box>
				</Box>
			</Box>
		</>
	)
}
